import { authHeaders, type AuthArgs } from './authArgs.ts';

export interface RoutingCandidate {
  endpointId: string;
  modelName: string;
  deployment: string;
  priority: number;
  quotaTpm: number;
  reason: string;
  panic: boolean;
  spilledFromTier: string | null;
}

export interface RoutingAttempt {
  kind: string;
  outcome: string;
  statusCode: number | null;
  elapsedMilliseconds: number;
}

export interface RoutingDecision {
  id: string;
  callId?: string | null;
  traceId: string | null;
  spanId: string | null;
  startedAtUtc: string;
  agentRef: string;
  requestedModel: string;
  estimatedTokens: number;
  discoveryEnabled: boolean;
  outcome: string;
  elapsedMilliseconds: number;
  truncated: boolean;
  candidates: RoutingCandidate[];
  attempt: RoutingAttempt | null;
}

export interface RoutingDecisionsResponse {
  replica: string;
  enabled: boolean;
  sampleRate: number;
  maxRecords: number;
  retentionSeconds: number;
  droppedRecords: number;
  truncatedRecords?: number;
  queueCapacity?: number;
  maxCandidates?: number;
  records: RoutingDecision[];
}

export function normalizeRoutingTraceId(value: string): string {
  const traceId = value.trim().toLowerCase();
  if (traceId && (!/^[0-9a-f]{32}$/.test(traceId) || /^0+$/.test(traceId))) {
    throw new Error('Enter a nonzero 32-character hexadecimal trace ID, or leave it empty.');
  }
  return traceId;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasNumbers(value: Record<string, unknown>, keys: string[]): boolean {
  return keys.every((key) => typeof value[key] === 'number' && Number.isFinite(value[key]));
}

function hasStrings(value: Record<string, unknown>, keys: string[]): boolean {
  return keys.every((key) => typeof value[key] === 'string');
}

function nullableString(value: unknown): boolean {
  return value === null || typeof value === 'string';
}

function validDecision(value: unknown): boolean {
  if (!isObject(value) || !hasStrings(value, ['id', 'startedAtUtc', 'agentRef', 'requestedModel', 'outcome'])
    || !hasNumbers(value, ['estimatedTokens', 'elapsedMilliseconds'])
    || !nullableString(value.traceId) || !nullableString(value.spanId)
    || (value.callId !== undefined && !nullableString(value.callId))
    || typeof value.discoveryEnabled !== 'boolean' || typeof value.truncated !== 'boolean'
    || !Array.isArray(value.candidates)) return false;
  if (!value.candidates.every((candidate: unknown) => isObject(candidate)
    && hasStrings(candidate, ['endpointId', 'modelName', 'deployment', 'reason'])
    && hasNumbers(candidate, ['priority', 'quotaTpm']) && typeof candidate.panic === 'boolean'
    && nullableString(candidate.spilledFromTier))) return false;
  const attempt = value.attempt;
  return attempt === null || (isObject(attempt) && hasStrings(attempt, ['kind', 'outcome'])
    && hasNumbers(attempt, ['elapsedMilliseconds'])
    && (attempt.statusCode === null || hasNumbers(attempt, ['statusCode'])));
}

function validReport(value: unknown): value is RoutingDecisionsResponse {
  return isObject(value) && typeof value.replica === 'string' && typeof value.enabled === 'boolean'
    && hasNumbers(value, ['sampleRate', 'maxRecords', 'retentionSeconds', 'droppedRecords'])
    && ['truncatedRecords', 'queueCapacity', 'maxCandidates'].every((key) => value[key] === undefined || hasNumbers(value, [key]))
    && Array.isArray(value.records) && value.records.every(validDecision);
}

export async function getRoutingDecisions(
  auth: AuthArgs,
  { traceId = '', limit = 50, signal }: { traceId?: string; limit?: number; signal?: AbortSignal } = {},
): Promise<RoutingDecisionsResponse> {
  const normalized = normalizeRoutingTraceId(traceId);
  if (!Number.isInteger(limit) || limit < 1 || limit > 100) {
    throw new Error('The record limit must be an integer from 1 to 100.');
  }
  const query = new URLSearchParams({ limit: String(limit) });
  if (normalized) query.set('traceId', normalized);
  const response = await fetch(`/api/v1/admin/routing/decisions?${query}`, {
    method: 'GET', headers: authHeaders(auth), cache: 'no-store', signal,
  });
  if (!response.ok) {
    // Do not render upstream response bodies: diagnostics errors must not expose customer details.
    switch (response.status) {
      case 400: throw new Error('The routing query was rejected. Check the trace ID and record limit.');
      case 401: throw new Error('Sign in again to view routing decisions.');
      case 403: throw new Error('You need the Admin.Partners permission to view routing decisions.');
      case 404: throw new Error('Routing decisions are not available on this API deployment.');
      default: throw new Error(`Could not load routing decisions (HTTP ${response.status}). Try refreshing.`);
    }
  }
  const body: unknown = await response.json().catch(() => null);
  if (!validReport(body)) throw new Error('The API returned an invalid routing diagnostics response. Try refreshing.');
  return body;
}
